import { useAuth } from "@/lib/contexts/auth-context";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function AuthRedirect({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect if still loading
    if (loading) return;

    // If we don't yet have a profile (still fetching in background), avoid redirects
    if (user && profile === undefined) return;

    // Don't redirect if we're on an auth page (unless we have a user and profile)
    const isAuthPage =
      location.pathname.startsWith("/auth/") ||
      location.pathname === "/signin" ||
      location.pathname === "/signup";

    // If user is authenticated and has a profile, redirect from certain pages
    if (user && profile) {
      // Check if profile setup is needed (non-owners with incomplete profile)
      // If so, don't redirect - let the callback page handle sending them to /profile
      const needsProfileSetup = !profile.is_owner && (!profile.full_name || !profile.username);
      if (needsProfileSetup && location.pathname.startsWith("/auth/")) {
        // Don't interfere with auth callback redirecting to profile setup
        return;
      }

      // Check for stored return URL (from OAuth flow)
      const returnUrl = sessionStorage.getItem("returnUrl");
      if (returnUrl && isAuthPage) {
        sessionStorage.removeItem("returnUrl");
        navigate(returnUrl, { replace: true });
        return;
      }

      const hasPortalAccess = profile.is_owner || (profile.staffRoles && profile.staffRoles.length > 0);
      const destination = hasPortalAccess ? "/owner/dashboard" : "/dashboard";

      // Redirect from home page
      if (location.pathname === "/") {
        navigate(destination, { replace: true });
      }
      // Redirect from auth pages
      else if (isAuthPage) {
        navigate(destination, { replace: true });
      }
    }
  }, [user, profile, loading, location.pathname, navigate]);

  // Avoid flashing intermediate pages while profile is loading post-auth
  if (user && (loading || profile === undefined)) {
    return null;
  }

  return <>{children}</>;
}
